import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Star, Lightbulb, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

function Stars({ value, onChange, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={() => onChange(n)} type="button">
          <Star className={`w-6 h-6 ${n <= value ? 'text-[#C99A55] fill-[#C99A55]' : 'text-[#E9DDCC]'}`} />
        </button>
      ))}
    </div>
  );
}

export default function ClientExtras() {
  const [events, setEvents] = useState([]);
  const [pending, setPending] = useState([]);
  const [fbOpen, setFbOpen] = useState(false);
  const [fb, setFb] = useState({ rating_seance: 0, rating_coach: 0, rating_studio: 0, comment: '' });
  const [idea, setIdea] = useState('');
  const [ideaOpen, setIdeaOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [e, p] = await Promise.all([
        axios.get(`${API}/client/events`, { withCredentials: true }),
        axios.get(`${API}/client/feedback-pending`, { withCredentials: true }),
      ]);
      setEvents(e.data.events); setPending(p.data.pending);
    } catch (err) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitFb = async () => {
    if (!fb.rating_seance || !fb.rating_coach || !fb.rating_studio) return toast.error('Merci de renseigner les trois notes.');
    const inv = pending[0] || {};
    try {
      await axios.post(`${API}/client/feedback`, { ...fb, slot_id: inv.slot_id, date: inv.date }, { withCredentials: true });
      toast.success('Merci pour votre retour !');
      setFbOpen(false); setFb({ rating_seance: 0, rating_coach: 0, rating_studio: 0, comment: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); }
  };

  const registerEvent = async (eventId) => {
    try {
      const r = await axios.post(`${API}/client/events/${eventId}/register`, {}, { withCredentials: true });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); }
  };

  const submitIdea = async () => {
    if (!idea.trim()) return;
    try {
      await axios.post(`${API}/client/suggestions`, { text: idea }, { withCredentials: true });
      toast.success('Merci ! Votre idée a été transmise à PURO.');
      setIdea(''); setIdeaOpen(false);
    } catch (e) { toast.error('Erreur.'); }
  };

  return (
    <div className="space-y-4 mt-6">
      {pending.length > 0 && (
        <div className="bg-gradient-to-br from-[#F3EADA] to-[#EFE0C4] border border-[#C99A55]/40 rounded-2xl p-5" data-testid="feedback-card">
          <div className="flex items-center gap-2 mb-1">
            <PartyPopper className="w-4 h-4 text-[#C99A55]" />
            <p className="font-heading text-lg text-[#493329]">Bravo pour votre séance chez PURO !</p>
          </div>
          <p className="text-xs text-[#7A6455] mb-3">Dites-nous comment cela s'est passé en 30 secondes.</p>
          <Button onClick={() => setFbOpen(true)} className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-full h-9 text-xs" data-testid="feedback-open">Répondre au questionnaire</Button>
        </div>
      )}

      {events.length > 0 && (
        <div className="bg-white border border-[#E9DDCC] rounded-2xl p-5" data-testid="client-events">
          <p className="text-[10px] uppercase tracking-wider text-[#9A744C] font-semibold mb-2">Événements PURO à venir</p>
          {events.map((e) => (
            <div key={e.id} className="py-3 border-t border-[#EFE4D0]" data-testid={`client-event-${e.id}`}>
              <p className="text-sm font-medium text-[#493329]">{e.title}
                <span className="text-xs text-[#7A6455] font-normal"> · {e.date ? new Date(e.date + 'T00:00').toLocaleDateString('fr-FR') : 'Date à venir — communiquée prochainement'}</span>
              </p>
              {e.description && <p className="text-xs text-[#7A6455] mt-1 leading-relaxed">{e.description}</p>}
              {e.capacity && (
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  {e.my_registration ? (
                    <span className="text-xs px-3 py-1.5 rounded-full bg-[#EDF2EA] text-[#5C7057]" data-testid={`event-registered-${e.id}`}>
                      {e.my_registration === 'inscrite' ? '✓ Votre place est réservée' : 'Vous êtes en liste d\'attente'}
                    </span>
                  ) : e.remaining > 0 ? (
                    <Button onClick={() => registerEvent(e.id)} className="bg-[#9A744C] hover:bg-[#493329] text-white rounded-full h-9 text-xs" data-testid={`event-register-${e.id}`}>
                      Je réserve ma place
                    </Button>
                  ) : (
                    <Button onClick={() => registerEvent(e.id)} variant="outline" className="rounded-full h-9 text-xs border-[#DCC488] text-[#9A744C]" data-testid={`event-waitlist-${e.id}`}>
                      Complet — liste d'attente
                    </Button>
                  )}
                  <span className="text-[11px] text-[#AE9885]" data-testid={`event-remaining-${e.id}`}>
                    {e.remaining > 0 ? `${e.remaining} place${e.remaining > 1 ? 's' : ''} restante${e.remaining > 1 ? 's' : ''} sur ${e.capacity}` : `Complet (${e.capacity}/${e.capacity})`}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-[#E9DDCC] rounded-2xl p-5" data-testid="ideas-block">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-[#C99A55]" />
            <p className="font-heading text-[#493329]">Vos idées pour PURO</p>
          </div>
          <Button variant="outline" onClick={() => setIdeaOpen(!ideaOpen)} className="h-8 text-xs rounded-full border-[#E9DDCC]" data-testid="idea-toggle">Proposer une idée</Button>
        </div>
        {ideaOpen && (
          <div className="mt-3 space-y-2">
            <Textarea value={idea} onChange={(e) => setIdea(e.target.value)} placeholder="Un service, un accessoire, un événement, une amélioration..." className="rounded-xl border-[#E9DDCC] text-sm" data-testid="idea-text" />
            <Button onClick={submitIdea} className="bg-[#9A744C] hover:bg-[#493329] text-white rounded-xl h-9 text-xs" data-testid="idea-submit">Envoyer mon idée</Button>
          </div>
        )}
      </div>

      <Dialog open={fbOpen} onOpenChange={setFbOpen}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-normal text-[#493329]">Votre séance chez PURO</DialogTitle>
            <DialogDescription className="text-xs text-[#7A6455]">Vos réponses restent confidentielles.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {[['rating_seance', 'Comment s\'est passée votre séance ?'], ['rating_coach', 'L\'accompagnement de votre coach ?'], ['rating_studio', 'Votre expérience au studio ?']].map(([k, l]) => (
              <div key={k}>
                <p className="text-sm text-[#493329] mb-1.5">{l}</p>
                <Stars value={fb[k]} onChange={(v) => setFb({ ...fb, [k]: v })} testid={`stars-${k}`} />
              </div>
            ))}
            <Textarea value={fb.comment} onChange={(e) => setFb({ ...fb, comment: e.target.value })} placeholder="Un commentaire ? (facultatif)" className="rounded-xl border-[#E9DDCC] text-sm" data-testid="feedback-comment" />
            <Button onClick={submitFb} className="w-full bg-[#9A744C] hover:bg-[#493329] text-white rounded-xl h-10" data-testid="feedback-submit">Envoyer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
